import React, { useState } from "react";
import { Activity, Globe2, RadioTower, ShieldCheck } from "lucide-react";
import type { LucideIcon } from "lucide-react";
import { FintheonBrandLockup } from "../brand/FintheonBrandLockup";
import { LoadingGlobeStage } from "../loading/LoadingGlobeStage";
import { useTheme } from "../../contexts/ThemeContext";
import { brandForTheme } from "./auth-brand-theme";
import { FluidCursor } from "./FluidCursor";
import { GoogleSignInButton } from "./GoogleSignInButton";
import { MagicLinkSignInForm } from "./MagicLinkSignInForm";
import { readAuthOnboardingState } from "./auth-onboarding-copy";
import { authShellCss } from "./auth-shell-styles";
import { isForumInviteUrl } from "../../lib/forum-invite-deeplink";
import { signInWithMagicLink } from "../../lib/supabase";

type AuthShellProps = {
  onSignIn: () => void;
  onSkipAuth?: () => void;
  isLoading?: boolean;
  intent?: "default" | "beta-download";
};

export const AuthShell: React.FC<AuthShellProps> = ({
  onSignIn,
  isLoading = false,
  intent = "default",
}) => {
  const { theme } = useTheme();
  const [submitted, setSubmitted] = useState(false);
  const [showLoader, setShowLoader] = useState(false);
  const [onboardingCopy] = useState(readAuthOnboardingState);
  const [forumInviteActive] = useState(() =>
    typeof window === "undefined"
      ? false
      : isForumInviteUrl(window.location.href),
  );
  function handleSignIn() {
    if (submitted) return;
    setSubmitted(true);
    setShowLoader(true);
    void onSignIn();
  }

  const busy = isLoading || submitted;
  const betaDownloadActive = intent === "beta-download";
  const showOnboardingCopy =
    !submitted &&
    (betaDownloadActive ||
      forumInviteActive ||
      (!onboardingCopy.isReturning && onboardingCopy.message));
  const headline = betaDownloadActive
    ? "Create your beta account."
    : forumInviteActive
      ? "Enter the Forum"
      : onboardingCopy.message;
  const points = betaDownloadActive
    ? BETA_DOWNLOAD_POINTS
    : forumInviteActive
      ? FORUM_INVITE_POINTS
      : LOGIN_POINTS;
  const authBrand = brandForTheme(theme.primary ?? theme.accent);

  return (
    <div className="auth-shell">
      <style>{authShellCss}</style>
      <LoadingGlobeStage
        phase={submitted ? "auth" : "idle"}
        density={1.08}
        className="auth-shell__globe"
        style={{
          opacity: "var(--auth-globe-opacity)",
        }}
      />
      <FluidCursor />
      <div className="auth-shell__wash" />
      <div className="auth-shell__grain" />
      <main className="auth-shell__content">
        {showOnboardingCopy ? (
          <section
            className="auth-shell__copy"
            aria-labelledby="fintheon-auth-title"
          >
            <h1 id="fintheon-auth-title">{headline}</h1>
            <div className="auth-shell__points" aria-label="Platform strengths">
              {points.map(({ Icon, label }) => (
                <div className="auth-shell__point" key={label}>
                  <span className="auth-shell__point-icon" aria-hidden="true">
                    <Icon size={15} strokeWidth={1.9} />
                  </span>
                  <span>{label}</span>
                </div>
              ))}
            </div>
          </section>
        ) : null}
        <section className="auth-shell__actions" aria-label="Sign in options">
          <header
            className="auth-shell__brand"
            aria-label="Fintheon"
            style={{ color: authBrand.color }}
          >
            <FintheonBrandLockup
              ariaLabel="Fintheon"
              className="auth-shell__brand-lockup"
              iconClassName="auth-shell__brand-icon"
              textClassName="auth-shell__brand-title"
              iconTone={authBrand.iconTone}
            />
          </header>
          <GoogleSignInButton
            label={showLoader ? "Opening Chrome" : "Continue with Google"}
            onClick={handleSignIn}
            isLoading={isLoading || showLoader}
            disabled={busy}
          />
          <MagicLinkSignInForm
            onSend={signInWithMagicLink}
            disabled={isLoading || showLoader}
          />
          <p>
            {betaDownloadActive
              ? "Create an account or sign in. Your Fintheon Desktop download is next."
              : forumInviteActive
                ? "Forum is free to listen. Sign in or create an account first."
                : "Macro, futures, catalysts, and desk discipline in one terminal."}
          </p>
        </section>
      </main>
    </div>
  );
};

const LOGIN_POINTS: Array<{ Icon: LucideIcon; label: string }> = [
  { Icon: Globe2, label: "Multi-asset intelligence" },
  { Icon: RadioTower, label: "Real-time narrative alerts" },
  { Icon: Activity, label: "Agent-led market reads" },
  { Icon: ShieldCheck, label: "Built-in desk discipline" },
];

const FORUM_INVITE_POINTS: Array<{ Icon: LucideIcon; label: string }> = [
  { Icon: RadioTower, label: "Live Forum floor" },
  { Icon: ShieldCheck, label: "Login or signup required" },
  { Icon: Activity, label: "Free listen-in access" },
];

const BETA_DOWNLOAD_POINTS: Array<{ Icon: LucideIcon; label: string }> = [
  { Icon: ShieldCheck, label: "Private beta account" },
  { Icon: Globe2, label: "Secure browser handoff" },
  { Icon: Activity, label: "Fintheon Desktop for macOS" },
];
